import React from "react";
import { ResizeSplit } from "./ResizeSplit";
import "./Profiling.scss";
import type { ProfileData, ProfilerNodeTiming } from "@recursive-robot/react-jsx-parser";
import { Button, Switch, FormControlLabel } from "../../../shared/components";
import { useAppSelector, useThunkDispatch } from "../../../store";
import {
    setProfilingEnabled,
    setReactRenderEnabled,
    clearProfiling,
} from "../../../store/profiling/actions";
import { selectSelectedCycle } from "../../../store/profiling/selectors";
import { isProfilingBuild, setProfilingBuildCookie } from "../../../shared/profiling-build";
import CycleTree from "./CycleTree";
import TriggeredBy from "./TriggeredBy";
import FlameGraph from "./FlameGraph";
import ProfileTimeline from "./ProfileTimeline";
import NodeDetail from "./NodeDetail";
import AggregateView from "./AggregateView";
import { cycleBatches, batchKey } from "./viz";

type ViewMode = "passes" | "aggregate";

function Profiling() {
    const dispatch = useThunkDispatch();
    const enabled = useAppSelector(s => s.profiling.enabled);
    const reactRenderEnabled = useAppSelector(s => s.profiling.reactRenderEnabled);
    const groupCount = useAppSelector(s => s.profiling.groupOrder.length);
    const selectedCycle = useAppSelector(selectSelectedCycle);
    const selectedCycleId = useAppSelector(s => s.profiling.selectedCycleId);

    const [viewMode, setViewMode] = React.useState<ViewMode>("passes");
    const [selectedBatchKey, setSelectedBatchKey] = React.useState<string | null>(null);
    const [selectedNodeId, setSelectedNodeId] = React.useState<number | null>(null);

    // The batch currently shown in the flame graph: the one clicked in the timeline, else the cycle's
    // render batch, else its first available batch.
    const batches = React.useMemo(() => (selectedCycle ? cycleBatches(selectedCycle) : []), [selectedCycle]);
    const activeBatch: ProfileData | undefined = React.useMemo(() => {
        if (!batches.length) return undefined;
        return batches.find(b => batchKey(b) === selectedBatchKey) ?? selectedCycle?.render ?? batches[0];
    }, [batches, selectedBatchKey, selectedCycle]);

    // Reset the batch/node selection when switching cycles.
    React.useEffect(() => { setSelectedBatchKey(null); setSelectedNodeId(null); }, [selectedCycleId]);

    const selectedNode: ProfilerNodeTiming | null = React.useMemo(() => {
        if (!activeBatch || selectedNodeId == null) return null;
        return activeBatch.nodes.find(n => n.id === selectedNodeId) ?? null;
    }, [activeBatch, selectedNodeId]);

    // The toggle persists in localStorage but the cookie can be cleared independently — re-sync it
    // so the reload hint below can always be resolved by actually reloading.
    React.useEffect(() => { setProfilingBuildCookie(reactRenderEnabled); }, [reactRenderEnabled]);

    // Toggling "React render timing" switches builds via cookie, which only takes effect on the
    // next page load. The dev server needs no hint — React's development build already profiles.
    const reloadHint =
        reactRenderEnabled && !isProfilingBuild && !import.meta.env.DEV
            ? "Reload the page to load the React profiling build."
            : !reactRenderEnabled && isProfilingBuild
                ? "Reload the page to return to the standard (faster) build."
                : null;

    return (
        <div className="profiling-layout">
            <div className="profiling-toolbar">
                <FormControlLabel
                    control={<Switch checked={enabled} onChange={(_e, v) => dispatch(setProfilingEnabled(v))} />}
                    label="Enable profiling"
                />
                <span title="Also profiles each custom component via React.Profiler. Toggling this serves the React profiling build (via cookie) — reload the page for the switch to take effect.">
                    <FormControlLabel
                        control={<Switch checked={reactRenderEnabled} disabled={!enabled} onChange={(_e, v) => dispatch(setReactRenderEnabled(v))} />}
                        label="React render timing"
                    />
                </span>
                {reloadHint && <span className="profiling-reload-hint">{reloadHint}</span>}

                <span className="toolbar-spacer" />

                <span className="cycle-count">{groupCount} pass{groupCount === 1 ? "" : "es"}</span>
                <div className="view-toggle">
                    <Button size="small" variant={viewMode === "passes" ? "contained" : "outlined"} onClick={() => setViewMode("passes")}>Passes</Button>
                    <Button size="small" variant={viewMode === "aggregate" ? "contained" : "outlined"} onClick={() => setViewMode("aggregate")}>Aggregate</Button>
                </div>
                <Button size="small" variant="contained" color="secondary" disabled={groupCount === 0} onClick={() => dispatch(clearProfiling())}>Clear</Button>
            </div>

            {viewMode === "aggregate" ? (
                <AggregateView />
            ) : (
                // Resizable panels: list | detail (horizontal); within detail, timeline / flame-area
                // (vertical); within flame-area, flame graph | node detail (horizontal). Uses a custom
                // pointer-capture splitter (ResizeSplit) so dragging works inside the pop-out window.
                // The split structure stays mounted; only pane content is conditional.
                <ResizeSplit className="profiling-body" direction="horizontal" initialSizes={[20, 80]} minSizes={[220, 360]} gutterSize={8}>
                    <div className="cycle-list">
                        <CycleTree />
                    </div>

                    <div className="detail-column" style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
                        <TriggeredBy selectedCycle={selectedCycle} />
                        <ResizeSplit className="detail-split" direction="vertical" initialSizes={[26, 74]} minSizes={[70, 200]}>
                            <div className="timeline-pane">
                                {selectedCycle && (
                                    <ProfileTimeline
                                        cycle={selectedCycle}
                                        selectedBatchKey={activeBatch ? batchKey(activeBatch) : null}
                                        onSelectBatch={b => { setSelectedBatchKey(batchKey(b)); setSelectedNodeId(null); }}
                                    />
                                )}
                            </div>
                            <ResizeSplit className="flame-and-detail" direction="horizontal" initialSizes={[66, 34]} minSizes={[260, 220]}>
                                <div className="flame-pane">
                                    {!selectedCycle && <div className="cycle-detail-empty">Select a render pass on the left.</div>}
                                    {selectedCycle && (activeBatch
                                        ? <FlameGraph batch={activeBatch} selectedNodeId={selectedNodeId} onSelectNode={setSelectedNodeId} />
                                        : <div className="cycle-detail-empty">This pass produced no timing nodes.</div>)}
                                </div>
                                <div className="detail-pane">
                                    {selectedCycle && activeBatch && <NodeDetail batch={activeBatch} node={selectedNode} />}
                                </div>
                            </ResizeSplit>
                        </ResizeSplit>
                    </div>
                </ResizeSplit>
            )}
        </div>
    );
}

export default React.memo(Profiling);
